<script setup lang="ts">
/**
 * A section or screen with nothing to show, or that cannot load: the title in
 * Title/Large, a muted line under it and, in `actions`, the way on (usually one
 * `soft` UiButton) — centred in the space it is given (`flex-1` in a column).
 * The title is an `<h2>` under the page's `<h1>`; on a screen with no page
 * title (an error or not-found screen) it is the page's heading, so pass
 * `:heading-level="1"`. Pass `role="alert"` when it replaces content because
 * something failed.
 * Figma: OMB Kit › Empty State.
 */
const props = withDefaults(defineProps<{
  title: string;
  description?: string;
  headingLevel?: 1 | 2;
}>(), {
  description: undefined,
  headingLevel: 2,
});

defineSlots<{
  actions?: () => unknown;
}>();
</script>

<template>
  <div
    class="flex flex-1 flex-col items-center justify-center gap-3 text-center"
    data-testid="ui-empty-state"
  >
    <component
      :is="`h${props.headingLevel}`"
      class="text-title-lg font-semibold"
      data-testid="ui-empty-state-title"
    >
      {{ props.title }}
    </component>
    <!-- The body's own 16px / 1.5, as v1's empty screens: no type style. -->
    <p
      v-if="props.description"
      class="text-fg-muted"
    >
      {{ props.description }}
    </p>
    <div
      v-if="$slots.actions"
      class="mt-3 flex flex-wrap justify-center gap-3"
    >
      <slot name="actions" />
    </div>
  </div>
</template>
